import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useLabs } from "@/hooks/use-labs";
import {
  LabDirectoryFilters,
  type LabDirectoryFilterValues,
} from "@/components/labs/lab-directory-filters";
import { LabDirectoryTable } from "@/components/labs/lab-directory-table";
import {
  Breadcrumbs,
  Button,
  EmptyState,
  PageHeader,
  Pagination,
  SkeletonTable,
} from "@/components/ui";

export const Route = createFileRoute("/_authenticated/labs/")({
  component: LabDirectoryPage,
});

const PAGE_SIZE = 25;

function LabDirectoryPage() {
  const { t } = useTranslation(["labs", "common", "org"]);
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);

  const [filters, setFilters] = useState<LabDirectoryFilterValues>({});
  const [page, setPage] = useState(0);

  const { data, isLoading, isError, error } = useLabs(orgId, {
    status: filters.status,
    relationship_type: filters.relationshipType,
    search: filters.search,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const labs = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("title") }]} />
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle")}
        actions={<Button onClick={() => void navigate({ to: "/labs/new" })}>{t("addLab")}</Button>}
      />

      <div className="mt-6">
        <LabDirectoryFilters
          filters={filters}
          onChange={(next) => {
            setFilters(next);
            setPage(0);
          }}
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("empty.description")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={5} rows={5} />
        </div>
      ) : labs.length === 0 ? (
        <div className="mt-6">
          <EmptyState title={t("empty.title")} description={t("empty.description")} />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <LabDirectoryTable
              labs={labs}
              onRowClick={(lab) => void navigate({ to: "/labs/$labId", params: { labId: lab.id } })}
            />
          </div>
          <Pagination
            currentPage={page + 1}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setPage(p - 1)}
          />
        </>
      )}
    </div>
  );
}
